import type { RollHistoryEntry } from '../rules/rollHistory.ts';

/**
 * Session-scoped state. NOT part of the permanent Character record, but it IS
 * saved to disk so an unfinished session survives closing the application.
 *
 * RULE DCC-SESS-001 - Rules for Survival p.12.
 * An untrained Skill Check made on the Tutorial Floors (1-2) is written down.
 * At the end of a 2-hour session the crawler gains 1 Rank in ONE of the Skills
 * attempted untrained that session. The list is then cleared and cannot carry
 * over to a later session.
 *
 * The session also owns the dice-roll history (RULE DCC-HIST-001), because
 * recent rolls belong to the sitting, not to the character sheet.
 */
export const SESSION_SCHEMA_VERSION = 5;

export interface SessionState {
  schemaVersion: number;
  characterId: string;
  startedAt: string;
  updatedAt: string;
  /** Skill content IDs attempted untrained during this session. Deduplicated. */
  untrainedSkillsAttempted: string[];
  /** Newest first. Capped at MAX_ROLL_HISTORY. */
  rollHistory: RollHistoryEntry[];
  /**
   * An untrained Skill award snapshotted at a 2-hour checkpoint and not yet
   * resolved by the player.
   *
   * RULE DCC-SESS-002 - the candidates belong to the two-hour INTERVAL that
   * just closed, not to the whole sitting. Holding them here keeps intervals
   * from mixing: `untrainedSkillsAttempted` immediately starts collecting the
   * next interval's attempts while this award waits to be claimed.
   */
  pendingUntrainedAward: { candidates: string[]; openedAt: string } | null;
}

export function createSession(characterId: string, startedAt: string): SessionState {
  return {
    schemaVersion: SESSION_SCHEMA_VERSION,
    characterId,
    startedAt,
    updatedAt: startedAt,
    untrainedSkillsAttempted: [],
    rollHistory: [],
    pendingUntrainedAward: null,
  };
}

export interface SessionValidation {
  valid: boolean;
  errors: string[];
}

export function validateSession(value: unknown): SessionValidation {
  const errors: string[] = [];
  if (typeof value !== 'object' || value === null) {
    return { valid: false, errors: ['Session record is not an object.'] };
  }
  const s = value as Record<string, unknown>;
  if (typeof s['characterId'] !== 'string' || s['characterId'].length === 0) {
    errors.push('Session is missing a characterId.');
  }
  if (typeof s['schemaVersion'] !== 'number') {
    errors.push('Session is missing a schemaVersion.');
  } else if (s['schemaVersion'] > SESSION_SCHEMA_VERSION) {
    errors.push('Session was saved by a newer version of the app.');
  }
  if (!Array.isArray(s['untrainedSkillsAttempted'])) {
    errors.push('untrainedSkillsAttempted must be a list.');
  }
  if (!Array.isArray(s['rollHistory'])) errors.push('rollHistory must be a list.');
  const pending = s['pendingUntrainedAward'];
  if (pending !== null && pending !== undefined) {
    if (typeof pending !== 'object' || !Array.isArray((pending as Record<string, unknown>)['candidates'])) {
      errors.push('pendingUntrainedAward must be null or hold a candidate list.');
    }
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Upgrades an older session record. Version 1 had no roll history and no
 * updatedAt. Never edit an existing step.
 */
export function migrateSessionRecord(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) return value;
  const record = { ...(value as Record<string, unknown>) };
  const version = typeof record['schemaVersion'] === 'number' ? record['schemaVersion'] : 0;

  if (version <= 1) {
    record['rollHistory'] = [];
    record['updatedAt'] = record['startedAt'];
    record['schemaVersion'] = 2;
  }
  if (record['schemaVersion'] === 2) {
    // 2 -> 3: history gained Attack, damage and Advancement roll types.
    // Existing entries are all d20 Checks and already have the right shape;
    // they only need the fields the newer types introduced.
    const history = Array.isArray(record['rollHistory']) ? record['rollHistory'] : [];
    record['rollHistory'] = history.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const e = entry as Record<string, unknown>;
      return { difficulty: null, degree: null, ...e };
    });
    record['schemaVersion'] = 3;
  }
  if (record['schemaVersion'] === 3) {
    // 3 -> 4: 2-hour checkpoints now snapshot their untrained award instead of
    // ending the session. An existing session has no snapshot in flight.
    record['pendingUntrainedAward'] = null;
    record['schemaVersion'] = 4;
  }
  if (record['schemaVersion'] === 4) {
    // 4 -> 5: rolls now record which Effects contributed and what the player
    // originally asked for. Older rolls were made before Effects existed, so
    // they contributed none, and the mode used was the mode chosen.
    const history = Array.isArray(record['rollHistory']) ? record['rollHistory'] : [];
    record['rollHistory'] = history.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const e = entry as Record<string, unknown>;
      return { effectSources: [], manualMode: e['mode'] ?? 'normal', ...e };
    });
    record['schemaVersion'] = 5;
  }
  return record;
}
